import React, { useState, useEffect, useRef, useContext } from 'react';
import { Menu, X, ChevronDown, ChevronRight, Moon, Sun, LogOut, CalendarDays, BookOpen, Briefcase, Newspaper, Phone, HelpCircle, MessageCircle, Building2, Flame, ShieldCheck, Hotel, Plane, Map, MapPin, UserPlus, LogIn, Download, Navigation, Heart } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LANGUAGES } from '../../i18n';
import { currencies, countries } from '../../data/geoData';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { useSiteBrand } from '../../context/SiteBrandContext';
import { useB2BAgent } from '../../context/B2BAgentContext';
import { usePWAInstall } from '../../hooks/usePWAInstall';
// isAllowedUser removed — authorization is now enforced server-side during login
import { useGoogleLogin } from '@react-oauth/google';
import { API_BASE_URL } from '../../constants';
import { GoogleReadyContext } from '../../App';
import { hasActiveHeroImages, isMobileViewport } from '../../utils/heroImages';
import { variantForcesWhiteNav, variantForcesDarkNav, variantPhoneLightHeader, readPreviewVariant } from '../../pages/Home/variants';
import { routePrefetchHandler } from '../../utils/prefetchRoutes';
import { optimizeImageUrl } from '../../utils/imageCdn';
import { readSiteFeatures } from '../../utils/siteFeatures';

// The currency / residency picker is only needed once someone opens it —
// loaded then (it was in the main bundle for every visitor).
const GlobalSelectorModal = React.lazy(() => import('../Common/GlobalSelectorModal'));

// ── Google login extracted to child component so useGoogleLogin() is only called
// when GoogleOAuthProvider is mounted (i.e. when a real clientId is available).
const SPINNER_SVG = <svg className="animate-spin" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" /></svg>;
const GOOGLE_SVG_COLOR = <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.14 0 5.95 1.08 8.17 2.84l6.1-6.1C34.46 3.19 29.52 1 24 1 14.82 1 7.07 6.48 3.64 14.22l7.1 5.52C12.5 13.67 17.8 9.5 24 9.5z" /><path fill="#4285F4" d="M46.52 24.5c0-1.56-.14-3.07-.4-4.5H24v8.5h12.67c-.55 2.95-2.2 5.44-4.68 7.12l7.14 5.54C43.37 37.19 46.52 31.36 46.52 24.5z" /><path fill="#FBBC05" d="M10.74 28.26A14.53 14.53 0 0 1 9.5 24c0-1.48.25-2.91.74-4.26l-7.1-5.52A23.93 23.93 0 0 0 0 24c0 3.86.92 7.5 2.55 10.72l8.19-6.46z" /><path fill="#34A853" d="M24 47c5.52 0 10.15-1.83 13.54-4.96l-7.14-5.54C28.5 38.07 26.36 38.5 24 38.5c-6.2 0-11.5-4.17-13.26-9.74l-8.19 6.46C6.07 42.48 14.48 47 24 47z" /></svg>;
const GOOGLE_SVG_WHITE = <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#fff" d="M24 9.5c3.14 0 5.95 1.08 8.17 2.84l6.1-6.1C34.46 3.19 29.52 1 24 1 14.82 1 7.07 6.48 3.64 14.22l7.1 5.52C12.5 13.67 17.8 9.5 24 9.5z" /><path fill="#fff" d="M46.52 24.5c0-1.56-.14-3.07-.4-4.5H24v8.5h12.67c-.55 2.95-2.2 5.44-4.68 7.12l7.14 5.54C43.37 37.19 46.52 31.36 46.52 24.5z" /><path fill="#fff" d="M10.74 28.26A14.53 14.53 0 0 1 9.5 24c0-1.48.25-2.91.74-4.26l-7.1-5.52A23.93 23.93 0 0 0 0 24c0 3.86.92 7.5 2.55 10.72l8.19-6.46z" /><path fill="#fff" d="M24 47c5.52 0 10.15-1.83 13.54-4.96l-7.14-5.54C28.5 38.07 26.36 38.5 24 38.5c-6.2 0-11.5-4.17-13.26-9.74l-8.19 6.46C6.07 42.48 14.48 47 24 47z" /></svg>;

function GoogleLoginBtn({ signIn, onClose, variant, t }) {
  const [authLoading, setAuthLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setAuthLoading(true);
      setLoginError('');
      try {
        // Send the access token to our server for email verification + session issuance
        const result = await signIn(tokenResponse.access_token);
        if (result?.ok) {onClose?.(result);} else
        {setLoginError(result?.error || 'Access denied.');}
      } catch {
        setLoginError('Sign-in failed. Please try again.');
      } finally {
        setAuthLoading(false);
      }
    },
    onError: () => {setAuthLoading(false);setLoginError('Google sign-in was cancelled or failed.');}
  });
  if (variant === 'mobile') {
    return (
      <>
                <button onClick={() => {googleLogin();}} disabled={authLoading}
        className="w-full flex items-center justify-center gap-3 py-3.5 rounded-2xl bg-accent hover:bg-accent/90 text-white font-bold text-sm shadow-sm shadow-accent/30 transition-all disabled:opacity-60 active:scale-[0.98]">
                    {authLoading ? SPINNER_SVG : GOOGLE_SVG_WHITE}
                    {authLoading ? t('nav.signingIn') : t('nav.signInWith', { provider: 'Google' })}
                </button>
                {loginError && <p className="mt-2 text-xs text-red-500 text-center">{loginError}</p>}
            </>);

  }
  return (
    <>
            <button onClick={() => {setAuthLoading(true);googleLogin();}} disabled={authLoading}
      className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 transition-all font-semibold text-slate-700 dark:text-slate-200 text-sm shadow-sm disabled:opacity-60">
                {authLoading ? SPINNER_SVG : GOOGLE_SVG_COLOR}
                {authLoading ? 'Signing in…' : 'Continue with Google'}
            </button>
            {loginError && <p className="mt-2 text-xs text-red-500 text-center">{loginError}</p>}
        </>);

}

// The shared reader: it also sees the server-injected window.__HM_FEATURES__,
// so the first render already has the real features (this local copy only
// read localStorage — empty on a first visit).
const readFeatures = readSiteFeatures;
const readHeroBanner = () => {
  try {return JSON.parse(localStorage.getItem('hm_hero_banner') || '{}');} catch {return {};}
};
const isEnabled = (value) => value === true || value === 'true' || value === 1 || value === '1';

const PM_PAGE_IDS = ['about', 'careers', 'press', 'contact', 'support', 'faq'];

// Pages that are always available on every site regardless of plan tier or
// feature toggle. They can still be hidden via Page Manager (`enabled: false`)
// per-page, but never via the plan-level feature flag system. Mirrors the
// "no feature gate" entries in src/data/pageRegistry.js.
const ALWAYS_AVAILABLE_COMPANY_PAGES = new Set(['about', 'careers', 'press', 'contact', 'faq']);
const readPMEnabled = () => {
  const out = {};
  PM_PAGE_IDS.forEach((id) => {
    try {const v = JSON.parse(localStorage.getItem('hm_page_' + id) || '{}');out[id] = v.enabled !== false;}
    catch {out[id] = true;}
  });
  return out;
};

const Navbar = () => {
  const { isDark, toggleTheme } = useTheme();
  const { user, userSession, signOut, userSignIn, userSignOut, unifiedSignIn } = useAuth();
  const navigate = useNavigate();
  // The effective signed-in profile: admin user takes precedence, fall back to customer session
  const activeProfile = user || userSession;
  const { brand } = useSiteBrand();
  const { agent: b2bAgent, logout: b2bLogout, isB2B } = useB2BAgent();
  const { canInstall, isInstalled, install } = usePWAInstall();
  // The site's own Android app (APK) is offered to Android visitors instead of
  // the browser's "Install app"; nothing is offered inside the app itself
  // (android-app/ appends "TravelApp/" to its user agent).
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
  const inNativeApp = /TravelApp\//.test(ua);
  const apkUrl = /^https:\/\/[^\s<>"'`]+$/i.test(String(brand.androidAppUrl || '')) ? brand.androidAppUrl : '';
  const offerApk = !!apkUrl && /Android/i.test(ua) && !inNativeApp;
  const { t, i18n } = useTranslation();
  const [logoError, setLogoError] = useState(false);
  // Computed once on mount — viewport width never changes mid-session on mobile
  const isMobileDevice = useRef(typeof window !== 'undefined' && window.innerWidth < 768);

  // Reset error whenever the logo URL changes
  useEffect(() => {setLogoError(false);}, [brand.logoUrl]);

  // Publish the navbar's actual rendered height as the --navbar-h CSS var so
  // pages that need to reserve space below the fixed navbar (admin layout,
  // hero sections, blog page top-pad) use a value that matches reality.
  // Without this they fall back to the hardcoded 3.5rem default — which is
  // ~24px short of the real height when the navbar is in its expanded
  // (non-scrolled) state, causing visible overlap or gaps.
  useEffect(() => {
    if (typeof window === 'undefined' || !navRef.current) return undefined;
    const el = navRef.current;
    let lastH = 0;
    let raf = 0;
    const setH = () => {
      const h = Math.round(el.getBoundingClientRect().height || 0);
      // Only write when the height actually changes — avoids per-scroll-frame
      // layout thrash on the many sticky bars that read --navbar-h.
      if (h > 0 && h !== lastH) {
        lastH = h;
        document.documentElement.style.setProperty('--navbar-h', `${h}px`);
      }
    };
    const scheduleH = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(setH); };
    setH();
    let ro;
    try {
      ro = new ResizeObserver(setH);
      ro.observe(el);
    } catch {/* older browsers — initial measurement still applies */}
    window.addEventListener('resize', setH);
    // The navbar shrinks (py-5 → py-3) once the page is scrolled. That
    // class-driven height change does NOT reliably fire the ResizeObserver,
    // so --navbar-h would stay at the taller expanded value and every sticky
    // sub-bar (filter bars, property tabs, etc.) hangs ~15px below the navbar
    // with a visible gap. Re-measure on scroll (rAF-throttled, writes only on
    // a real change) so the var always tracks the live navbar height.
    window.addEventListener('scroll', scheduleH, { passive: true });
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', setH);
      window.removeEventListener('scroll', scheduleH);
      cancelAnimationFrame(raf);
    };
  }, []);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [loginDropdownOpen, setLoginDropdownOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [features, setFeatures] = useState(readFeatures);
  const [heroBanner, setHeroBanner] = useState(readHeroBanner);
  const [pmEnabled, setPMEnabled] = useState(readPMEnabled);
  // Reactive viewport flag — drives the device-aware hero-image check.
  // A mobile-only image counts as "has image" on phones but not desktop,
  // and the navbar text colour must reflect that live (resize, rotate).
  const [isMobile, setIsMobile] = useState(isMobileViewport);
  // Authoritative white-text signal from the hero itself (see Hero.js) —
  // the hero KNOWS what background it painted (server-injected config,
  // gradient theme, images…), so its verdict beats our localStorage sniffing
  // whenever it's present. Falls back gracefully when no hero is mounted.
  const [heroWhiteNav, setHeroWhiteNav] = useState(
    () => typeof window !== 'undefined' && !!window.__HM_HERO_WHITE_NAV__
  );
  // The chat bubble (z 9990) sat on top of the open phone menu, covering its
  // last items; a class on <html> lets the floating buttons step aside.
  useEffect(() => {
    document.documentElement.classList.toggle('hm-mobile-menu-open', isMobileMenuOpen);
    return () => document.documentElement.classList.remove('hm-mobile-menu-open');
  }, [isMobileMenuOpen]);
  useEffect(() => {
    const heroNavHandler = (e) => setHeroWhiteNav(!!e.detail?.white);
    window.addEventListener('hm-hero-nav-mode', heroNavHandler);
    return () => window.removeEventListener('hm-hero-nav-mode', heroNavHandler);
  }, []);
  useEffect(() => {
    const handler = () => {
      setFeatures(readFeatures());
      setPMEnabled(readPMEnabled());
      setHeroBanner(readHeroBanner());
    };
    window.addEventListener('hm-settings-loaded', handler);
    // Viewport class watcher — keeps `forceWhiteText` accurate as the
    // user crosses the 768px boundary. Without this the navbar text
    // could stay dark on mobile after a resize even though a
    // mobile-only hero image just became active.
    const mq = typeof window !== 'undefined' ? window.matchMedia('(max-width: 767px)') : null;
    const syncMq = () => setIsMobile(!!mq?.matches);
    if (mq?.addEventListener) mq.addEventListener('change', syncMq);else
    if (mq?.addListener) mq.addListener(syncMq);
    return () => {
      window.removeEventListener('hm-settings-loaded', handler);
      if (mq?.removeEventListener) mq.removeEventListener('change', syncMq);else
      if (mq?.removeListener) mq.removeListener(syncMq);
    };
  }, []);

  // Allow any component (MobileBottomNav, future "Sign in to continue"
  // CTAs on protected pages, etc.) to open the mobile hamburger menu via
  // a global event. Kept even though MobileBottomNav now uses its own
  // SignInSheet — convenient extension point for future flows that want
  // the full menu rather than just auth.
  useEffect(() => {
    const open = () => setIsMobileMenuOpen(true);
    window.addEventListener('hm-open-mobile-menu', open);
    return () => window.removeEventListener('hm-open-mobile-menu', open);
  }, []);
  const loginDropdownRef = useRef(null);
  const profileDropdownRef = useRef(null);
  const companyDropdownRef = useRef(null);
  const helpDropdownRef = useRef(null);
  const b2bDropdownRef = useRef(null);
  const navRef = useRef(null);
  // Explore dropdown — collects the gated travel sections (Deals, Hajj & Umrah,
  // Partner Hotels, Visa) which only show as inline chips on screens ≥ 1536px.
  // On standard laptops (1024–1535px) we surface them through this dropdown so
  // they remain reachable without forcing users to open the mobile hamburger.
  const exploreDropdownRef = useRef(null);
  const langDropdownRef = useRef(null);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const currentLang = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];
  const changeLang = (code) => {
    const previousLang = i18n.language || localStorage.getItem('hm_language') || 'en';
    const changed = previousLang !== code;
    if (!changed) return;
    localStorage.setItem('hm_language', code);
    setLangDropdownOpen(false);
    // i18next owns ALL translation now — every offered language ships real
    // locale files. Google Translate was removed (it machine-translated the
    // already-translated page, mangling correct Arabic into gibberish); the
    // only GT remnant is clearing its legacy cookie so returning visitors
    // stop being auto-mangled by anything cached.
    if (typeof window.clearGoogleTranslateCookies === 'function') {
      window.clearGoogleTranslateCookies();
    }
    window.location.reload();
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (loginDropdownRef.current && !loginDropdownRef.current.contains(e.target)) {
        setLoginDropdownOpen(false);
      }
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(e.target)) {
        setProfileDropdownOpen(false);
      }
      if (companyDropdownRef.current && !companyDropdownRef.current.contains(e.target)) {
        setActiveDropdown((prev) => prev === 'company' ? null : prev);
      }
      if (helpDropdownRef.current && !helpDropdownRef.current.contains(e.target)) {
        setActiveDropdown((prev) => prev === 'help' ? null : prev);
      }
      if (exploreDropdownRef.current && !exploreDropdownRef.current.contains(e.target)) {
        if (activeDropdown === 'explore') setActiveDropdown(null);
      }
      if (b2bDropdownRef.current && !b2bDropdownRef.current.contains(e.target)) {
        setActiveDropdown((prev) => prev === 'b2b' ? null : prev);
      }
      if (langDropdownRef.current && !langDropdownRef.current.contains(e.target)) {
        setLangDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const googleClientId = useContext(GoogleReadyContext);

  // Unified Modal State
  const [selectorModal, setSelectorModal] = useState({ isOpen: false, type: 'residency' });

  const [currency, setCurrency] = useState(() => {
    const saved = localStorage.getItem('user_currency');
    if (saved) {
      const parsed = JSON.parse(saved);
      // Migration: If flag is an emoji or not a simple ISO code, reset to default or find match
      if (parsed.flag && parsed.flag.length > 2) {
        const match = currencies.find((c) => c.code === parsed.code);
        return match || currencies[0];
      }
      return parsed;
    }
    return currencies[0];
  });
  const [residency, setResidency] = useState(() => {
    const saved = localStorage.getItem('user_residency');
    if (saved) {
      // Migration: Check if saved is an emoji/invalid code
      if (saved.length > 2) return countries[0];
      return countries.find((c) => c.code === saved) || countries[0];
    }
    return countries[0];
  });

  const updateResidency = (country) => {
    setResidency(country);
    localStorage.setItem('user_residency', country.code);
    window.dispatchEvent(new CustomEvent('residencyChanged', { detail: { countryCode: country.code } }));
  };

  const updateCurrency = (curr) => {
    setCurrency(curr);
    localStorage.setItem('user_currency', JSON.stringify(curr));
    window.dispatchEvent(new CustomEvent('currencyChanged', { detail: { currencyCode: curr.code } }));
  };

  const inferCurrencyForCountry = (countryCode) => {
    const code = String(countryCode || '').toLowerCase();
    if (!code) return null;

    const direct = currencies.find((c) => c.flag === code);
    if (direct) return direct;

    if (['fr', 'de', 'it', 'es', 'pt', 'gr', 'nl', 'be', 'at', 'fi'].includes(code)) {
      return currencies.find((c) => c.code === 'EUR') || null;
    }

    return null;
  };

  const handleGlobalSelect = (type, item) => {
    if (type === 'currency') updateCurrency(item);else
    updateResidency(item);
    setSelectorModal({ ...selectorModal, isOpen: false });
  };

  const location = useLocation();
  // Only these routes have a full-bleed dark hero the navbar overlaps (white text needed at top).
  // Every other route starts with a light/white background → dark text from the very first pixel.
  const DARK_HERO_ROUTES = new Set([
  '/', '/about', '/destinations', '/experiences',
  '/flights', '/hotels', '/nearby',
  '/hotels-partner', '/deals', '/blog', '/tours']
  );
  const normPath = location.pathname === '/' ? '/' : location.pathname.replace(/\/$/, '');
  const isLightPage = !DARK_HERO_ROUTES.has(normPath);
  // On admin pages, the slate dashboard bg sits directly behind the navbar.
  // Force a fully-opaque navbar so the slate doesn't bleed through the
  // backdrop-filter and look like a "gap" below the navbar.
  const isAdminPage = normPath.startsWith('/private');
  // AUTOMATIC force-white-text — PURE image-presence rule, PLUS the Gradient
  // hero theme which — like an image — paints a colorful background that
  // doesn't follow the sitewide dark-mode toggle, so the navbar can't rely on
  // `isDark` to guess the right contrast. (Simple White needs no special case:
  // it follows the site toggle, which is exactly what the default formula
  // below assumes.) Scoped to the homepage only: `heroTheme` is a Hero.js-only
  // setting, other DARK_HERO_ROUTES pages have their own separate banners.
  // No fallback to `heroBanner.forceWhiteText` here on purpose — that
  // legacy toggle may still be `true` in tenants' localStorage from
  // when it was the only way to control this. Trusting it would force
  // white nav text on desktop in scenarios where the admin set a
  // mobile-only image (no desktop picture → must be dark text on the
  // light page background).
  const onHomepage = normPath === '/';
  const isGradientHeroTheme = onHomepage && heroBanner?.heroTheme === 'gradient';
  // Some home variants (Trip's brand gradient, Horizon's midnight sky) paint
  // their own dark hero band unconditionally — in both light and dark mode,
  // with or without an admin-uploaded hero image — so they always need white
  // nav text while unscrolled, the same as the gradient hero theme above.
  // Which variants those are is declared in the variant registry
  // (src/pages/Home/variants.js → forceWhiteNav), NOT hardcoded here: a
  // hardcoded `=== 'trip'` check previously left Horizon falling back to the
  // legacy hasActiveHeroImages() check (built for the "classic" hero's
  // optional image), which reads a `bgImage`/`bgImageDevices` field these
  // variants don't use — whichever device that stale field happened to be
  // tagged for (or neither) decided the text colour essentially at random,
  // sometimes leaving dark text unreadable against the colored gradient.
  // A ?preview_design=<id> view uses that design's nav rules, not the saved one's.
  const homeVariant = (onHomepage && readPreviewVariant()) || heroBanner?.homeVariant;
  const isWhiteNavHomeVariant = onHomepage && variantForcesWhiteNav(homeVariant);
  // The inverse guard: a variant that paints its own LIGHT header (registry
  // forceDarkNav, e.g. the Airbnb-style theme) must never inherit white text
  // from hero images saved for the classic theme — white logo on a white
  // page is an invisible navbar.
  // …unless that theme is showing an admin hero PHOTO (its header is then a
  // dark image, not a white surface), in which case white text is correct.
  const isDarkNavHomeVariant = onHomepage && variantForcesDarkNav(homeVariant)
    && !hasActiveHeroImages(heroBanner, isMobile);
  // Phone-only light header (registry phoneLightHeader, e.g. Trip): below
  // 768px that theme's band starts under the navbar, so the navbar sits on the
  // light page and keeps dark text + the light-surface logo.
  // Pages Management → Home → "Home Page Extras" → Makkah hero: that theme's
  // photo hero is dark and a coloured logo vanished on it at desktop width
  // too, so with it on the light header applies at every width (HomeTrip.css
  // starts the photo under the navbar to match). Off by default.
  const isHolyHeroOn = heroBanner?.homeExtras?.holyHero === true;
  const isPhoneLightHeader = onHomepage && (isMobile || isHolyHeroOn) && variantPhoneLightHeader(homeVariant);
  const forceWhiteText = !isDarkNavHomeVariant && !isPhoneLightHeader && (hasActiveHeroImages(heroBanner, isMobile) || isGradientHeroTheme || isWhiteNavHomeVariant || (onHomepage && heroWhiteNav));
  const isTransparentHeroNav = !isLightPage && !isScrolled;
  const forceWhiteNavText = forceWhiteText && isTransparentHeroNav;
  // Force-white only applies while navbar is transparent over the hero.
  // Once the scrolled background appears, use normal readable nav text.
  const useDarkText = forceWhiteNavText ? false : isLightPage || isScrolled || !isDark && normPath === '/';

  // isDashboardUser: only true for admin users (who have dashboard access)
  const isDashboardUser = !!user;

  // IP-based Detection
  useEffect(() => {
    if (localStorage.getItem('user_residency') || localStorage.getItem('user_residency_failed')) return;

    const detectLocation = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/geo-ip`);
        if (!response.ok) throw new Error('Network response was not ok');
        const data = await response.json();
        if (data?.ok) {
          const country =
          countries.find((c) => c.code.toLowerCase() === String(data.countryCode || '').toLowerCase()) ||
          countries.find((c) => c.name.toLowerCase() === String(data.country || '').toLowerCase());
          if (country) updateResidency(country);

          const curr =
          currencies.find((c) => c.code === String(data.currency || '').toUpperCase()) ||
          inferCurrencyForCountry(country?.code);
          if (curr) updateCurrency(curr);
        }
      } catch (err) {
        console.error("Location detection failed:", err);
        // Mark as failed for this session so we don't loop/spam the API
        localStorage.setItem('user_residency_failed', 'true');
      }
    };
    detectLocation();
  }, []);

  useEffect(() => {
    // Keep navbar text/background state synced even when the browser restores
    // scroll position, momentum-scroll ends, or the page regains focus.
    let rafId = null;
    const syncScrolledState = () => {
      const next = window.scrollY > 50;
      setIsScrolled((prev) => next === prev ? prev : next);
    };
    const scheduleSync = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        syncScrolledState();
        rafId = null;
      });
    };

    syncScrolledState();
    window.addEventListener('scroll', scheduleSync, { passive: true });
    window.addEventListener('scrollend', syncScrolledState, { passive: true });
    window.addEventListener('resize', syncScrolledState, { passive: true });
    window.addEventListener('focus', syncScrolledState);
    document.addEventListener('visibilitychange', syncScrolledState);

    return () => {
      window.removeEventListener('scroll', scheduleSync);
      window.removeEventListener('scrollend', syncScrolledState);
      window.removeEventListener('resize', syncScrolledState);
      window.removeEventListener('focus', syncScrolledState);
      document.removeEventListener('visibilitychange', syncScrolledState);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [location.pathname]);

  return (
    <>
            <nav
        ref={navRef}
        onMouseOver={routePrefetchHandler}
        onTouchStart={routePrefetchHandler}
        /* The mobile drawer is a CHILD of this <nav>, so its own z-index only
         * ever competes against its siblings inside the nav — it can never
         * escape the nav's z-50 in the page's actual stacking order. Every
         * hero-level search widget on the site sits well above that (up to
         * z-[200] for its suggestions dropdown), so with the nav pinned at a
         * flat z-50 the open mobile menu rendered BEHIND those widgets
         * instead of over them — confirmed from a screenshot on the Partner
         * Hotels page, the widget's fields visibly on top of "Tours" and
         * "Umrah Planner" in the open drawer.
         *
         * Bumping the nav's z-index only while the menu is open (z-[250],
         * comfortably clearing that z-[200] ceiling) fixes it without
         * touching the z-50 the header uses at every other time — nothing
         * else on the page currently needs the header above z-50 when the
         * drawer is closed, so this stays scoped to the one state that broke. */
        className={`fixed w-full ${isMobileMenuOpen ? 'z-[250]' : 'z-50'} transition-colors duration-200 ${isLightPage || isScrolled ?
        'shadow-sm py-3 border-b border-black/[0.06] dark:border-white/[0.07]' :
        'bg-transparent py-5'}`
        }
        style={{
          top: 'var(--tib-height, 0px)',
          ...(isLightPage || isScrolled ? {
            backgroundColor: isMobileDevice.current || isAdminPage ?
            'var(--hm-nav-bg, #fff)' // solid bg on mobile & /private — no bleed-through
            : 'color-mix(in srgb, var(--hm-nav-bg) 88%, transparent)',
            // backdrop-filter is extremely expensive on mobile (causes compositor thrash).
            // Skip on /private too — slate dashboard bg behind it would tint everything gray.
            ...(isMobileDevice.current || isAdminPage ? {} : {
              backdropFilter: 'blur(16px) saturate(160%)',
              WebkitBackdropFilter: 'blur(16px) saturate(160%)'
            }),
            color: 'var(--hm-nav-text)'
          } : {})
        }}>
        
                <div className="w-full px-2 md:px-3 lg:px-4 flex items-center gap-2 lg:gap-3">
                    {/* Left: Logo + quick links */}
                    <div className="flex items-center gap-3 min-w-0 shrink lg:shrink-0">
                    <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-2 min-w-0 py-1.5 -my-1.5 group notranslate" translate="no">
                        {/* Wide horizontal lockup (icon + bilingual name baked into ONE
                            image, KSA-agency style): render it alone and larger, and skip
                            the separate icon + text below — the name lives in the image.
                            Dark navbars get the dark twin when provided. */}
                        {brand.logoMode === 'wide' && (brand.logoWideUrl || brand.logoWideDarkUrl) && !logoError ?
              <img
                src={optimizeImageUrl(
                  // Which twin fits the bar BEHIND the logo right now?
                  // useDarkText=true means the bar has a solid surface — but in
                  // dark THEME that surface is dark slate, so only
                  // (useDarkText && !isDark) is genuinely a light background.
                  // Everything else (transparent hero, dark theme, scrolled dark
                  // bar) wants the dark twin, falling back to the light one.
                  (useDarkText && !isDark)
                    ? brand.logoWideUrl
                    : (brand.logoWideDarkUrl || brand.logoWideUrl),
                  // Bar height caps the lockup at ~80px tall / ≤640 CSS px wide;
                  // 640 device px covers it at 2x. Uploads were shipping at
                  // 1998px / 457 KiB — the audit's single biggest line item.
                  640
                )}
                alt={brand.siteName || 'Logo'}
                // Sized by LIMITS, not a fixed height: w-auto + h-auto with
                // max-h/max-w scale the image down keeping its shape and never
                // enlarge it past its real size. A lockup like ajwaaa's
                // (640×252) still lands at exactly 203×80 (183×72 on phones);
                // a thin wordmark (hassaantravel: 256×27, 9.5:1) used to be
                // forced to 80px tall = 640px wide, blurry and shoving the menu
                // away — now it shows at its real 256×27, at most 300px wide.
                className={`w-auto h-auto min-w-0 max-h-[72px] md:max-h-20 -my-4 md:-my-5 max-w-[220px] sm:max-w-[260px] xl:max-w-[300px] object-contain
                  ${!(useDarkText && !isDark) && !brand.logoWideDarkUrl ? 'bg-white/90 rounded-xl px-2.5 py-1' : ''}`}
                height={72}
                decoding="async"
                onError={() => setLogoError(true)} loading="lazy" /> :
              <>
                        {brand.logoUrl && !logoError ?
              <img
                src={brand.logoUrl?.includes('res.cloudinary.com/') ? brand.logoUrl.replace('/upload/', '/upload/w_80,c_fit,q_auto,f_auto/') : brand.logoUrl}
                alt={brand.siteName || 'Logo'}
                className="h-8 w-auto max-w-[120px] object-contain flex-shrink-0"
                width={120}
                height={32}
                decoding="async"
                onError={() => setLogoError(true)} loading="lazy" /> :


              <div className={`w-8 h-8 flex-shrink-0 rounded-tr-xl rounded-bl-xl flex items-center justify-center transition-colors ${useDarkText ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900' :
              'bg-white text-slate-900 group-hover:bg-emerald-400'}`
              }>
                                <span className="font-bold text-lg">{(brand.siteName || 'L')[0].toUpperCase()}</span>
                            </div>
              }
                        <span className={`notranslate font-serif font-bold tracking-tight text-lg md:text-lg lg:text-base xl:text-xl truncate max-w-[230px] sm:max-w-[280px] md:max-w-[180px] lg:max-w-[130px] xl:max-w-[240px] ${useDarkText ? 'text-slate-900 dark:text-white' : 'text-white'}`
              } title={brand.siteName}>
                            {brand.siteName}
                        </span>
              </>
              }
                    </Link>

                    {/* ── Explore dropdown (left-side, 1024-1535px) ──
                 Sits next to the logo on standard laptop screens, where
                 the inline chips below are hidden. Auto-hides at 2xl
                 (≥1536px) so it doesn't duplicate the inline chips. */}
                    {(features.deals !== false || features.hajjUmrah !== false || features.offlineHotels !== false || features.visa !== false) &&
            <div className="relative hidden lg:flex 2xl:hidden" ref={exploreDropdownRef}>
                            <button
                onClick={() => setActiveDropdown((d) => d === 'explore' ? null : 'explore')}
                className={`flex items-center gap-0.5 px-2 py-1.5 rounded-full text-[13.5px] font-bold transition-colors ${
                useDarkText ? 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800' :
                'text-white/85 hover:bg-white/10'}`
                }>

                                {t('nav.explore')}
                                <ChevronDown size={14} className={`transition-transform duration-200 ${activeDropdown === 'explore' ? 'rotate-180' : ''}`} />
                            </button>
                            {activeDropdown === 'explore' &&
              <div className="absolute top-full left-0 mt-3 w-56 bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-100 dark:border-slate-800 py-2 z-50 animate-fade-in-down">
                                    {features.deals !== false &&
                <Link to="/deals" onClick={() => setActiveDropdown(null)}
                className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-primary transition-colors">
                                            <Flame size={15} className="text-amber-500" /> {t('nav.deals')}
                                        </Link>
                }
                                    {features.hajjUmrah !== false &&
                <Link to="/hajj-umrah" onClick={() => setActiveDropdown(null)}
                className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-primary transition-colors">
                                            <Moon size={15} className="text-emerald-500" /> {t('nav.hajjUmrah')}
                                        </Link>
                }
                                    {features.offlineHotels !== false &&
                <Link to="/hotels-partner" onClick={() => setActiveDropdown(null)}
                className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-primary transition-colors">
                                            <Building2 size={15} className="text-sky-500" /> {t('nav.partnerHotels')}
                                        </Link>
                }
                                    {features.visa !== false &&
                <Link to="/visa" onClick={() => setActiveDropdown(null)}
                className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-primary transition-colors">
                                            <ShieldCheck size={15} className="text-blue-500" /> {t('nav.visaInfo')}
                                        </Link>
                }
                                </div>
              }
                        </div>
            }

                    {/* Left-side quick links — shown only on truly wide screens
                 (≥ 1536px / Tailwind `2xl`). On the 1024-1535px range the
                 Explore dropdown above carries the same items so they
                 remain reachable; both never render simultaneously. */}
                    <div className="hidden 2xl:flex items-center gap-0.5">
                        {features.deals !== false &&
              <Link to="/deals"
              className={`inline-flex items-center gap-1 text-[13.5px] font-bold px-2.5 py-1.5 rounded-full transition-all ${
              location.pathname === '/deals' ?
              'bg-amber-400/20 text-amber-500 dark:text-amber-300' :
              useDarkText ? 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800' :
              'text-white/80 hover:bg-white/10'}`
              }>
                                <Flame size={12} /> {t('nav.deals')}
                            </Link>
              }
                        {/* For Agencies quick link — only on platform owner's installation */}
                        {process.env.REACT_APP_SHOW_FOR_AGENCIES === 'true' &&
              <a href={process.env.REACT_APP_MARKETING_URL || 'https://travorahub.com/'} target="_blank" rel="noopener noreferrer"
              className={`inline-flex items-center gap-1 text-[13.5px] font-bold px-2.5 py-1.5 rounded-full transition-all ${
              useDarkText ? 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800' :
              'text-white/80 hover:bg-white/10'}`
              }>
                            <Building2 size={12} /> {t('nav.forAgencies')}
                        </a>
              }
                        {features.hajjUmrah !== false &&
              <Link to="/hajj-umrah"
              className={`inline-flex items-center gap-1 text-[13.5px] font-bold px-2.5 py-1.5 rounded-full transition-all ${
              location.pathname.includes('/hajj-umrah') ?
              'bg-emerald-400/20 text-emerald-600 dark:text-emerald-300' :
              useDarkText ? 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800' :
              'text-white/80 hover:bg-white/10'}`
              }>
                                <Moon size={12} /> {t('nav.hajjUmrah')}
                            </Link>
              }
                        {/* Umrah Planner moved into the Company dropdown */}
                        {features.offlineHotels !== false &&
              <Link to="/hotels-partner"
              className={`inline-flex items-center gap-1 text-[13.5px] font-bold px-2.5 py-1.5 rounded-full transition-all ${
              location.pathname.includes('/hotels-partner') ?
              'bg-sky-400/20 text-sky-600 dark:text-sky-300' :
              useDarkText ? 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800' :
              'text-white/80 hover:bg-white/10'}`
              }>
                                <Building2 size={12} /> {t('nav.partnerHotels')}
                            </Link>
              }
                        {features.visa !== false &&
              <Link to="/visa"
              className={`inline-flex items-center gap-1 text-[13.5px] font-bold px-2.5 py-1.5 rounded-full transition-all ${
              location.pathname.startsWith('/visa') ?
              'bg-blue-400/20 text-blue-600 dark:text-blue-300' :
              useDarkText ? 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800' :
              'text-white/80 hover:bg-white/10'}`
              }>
                                <ShieldCheck size={12} /> {t('nav.visaInfo')}
                            </Link>
              }
                    </div>
                    </div>{/* end left group */}

                    {/* Desktop Links */}
                    <div className="hidden lg:flex items-center gap-0 ms-auto">

                        {/* Main Nav Links with active underline.
                 Destinations moved into the Company dropdown. */}
                        {[
            { name: t('nav.hotels'), path: '/hotels', key: 'hotelsAny' },
            { name: t('nav.flights'), path: '/flights', key: 'flights' },
            // PKFARE — separate flight vertical with its own page + feature flag.
            // Distinct label so both can coexist when a site enables both.
            { name: t('nav.pkfareFlights', 'Air Tickets'), path: '/pkfare-flights', key: 'pkfareFlights' },
            { name: t('nav.tours'), path: '/tours', key: 'tours' },
            { name: t('nav.blog'), path: '/blog', key: 'blog' }].
            filter((item) => {
              // Special key 'hotelsAny' — show if either ratehawk OR webbeds is on
              if (item.key === 'hotelsAny') {
                if (features.ratehawk === false && features.webbeds === false) return false;
                if (features.ratehawk === true && features.contentApi === false) return false;
                return true;
              }
              if (features[item.key] === false) return false;
              return true;
            }).map((item) => {
              const active = location.pathname.startsWith(item.path);
              return (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`relative px-1 xl:px-1.5 py-2 text-[13.5px] xl:text-[14.5px] font-semibold tracking-[-0.012em] transition-colors group ${
                  active ?
                  useDarkText ? 'text-primary' : 'text-white' :
                  useDarkText ? 'text-slate-700 dark:text-slate-200 hover:text-primary' : 'text-white/80 hover:text-white'}`
                  }>
                  
                                    {item.name}
                                    <span className={`absolute bottom-0 left-2 right-2 h-0.5 rounded-full transition-all duration-200 ${
                  active ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0 group-hover:opacity-60 group-hover:scale-x-100'} ${

                  useDarkText ? 'bg-primary' : 'bg-white'}`
                  } />
                                </Link>);

            })}

                        {/* ── Divider ── */}
                        <div className={`w-px h-5 mx-0.5 ${useDarkText ? 'bg-slate-200 dark:bg-slate-700' : 'bg-white/20'}`} />

                        {/* ── Company dropdown ── */}
                        <div className="relative" ref={companyDropdownRef}>
                            <button
                onClick={() => setActiveDropdown((d) => d === 'company' ? null : 'company')}
                className={`flex items-center gap-0.5 px-1 xl:px-1.5 py-2 text-[13.5px] xl:text-[14.5px] font-semibold tracking-[-0.012em] transition-colors ${
                useDarkText ? 'text-slate-700 dark:text-slate-200 hover:text-primary' : 'text-white/80 hover:text-white'}`
                }>
                
                                {t('nav.company')}
                                <ChevronDown size={14} className={`transition-transform duration-200 ${activeDropdown === 'company' ? 'rotate-180' : ''}`} />
                            </button>
                            {activeDropdown === 'company' &&
              <div className="absolute top-full left-0 mt-3 w-56 bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-100 dark:border-slate-800 py-2 z-50 animate-fade-in-down">
                                    {[
                // Travel-related links — moved here from the main nav row
                // to keep the navbar uncluttered. Each gated by its own
                // feature flag (preserves existing visibility rules).
                { label: t('nav.umrahPlanner'), path: '/ummrah-planner', icon: Navigation, key: 'travel_ummrahPlanner' },
                { label: t('nav.destinations'), path: '/destinations', icon: MapPin, key: 'travel_destinations' },
                { label: t('nav.aboutUs'), path: '/about', icon: BookOpen, key: 'company_about' },
                { label: t('nav.careers'), path: '/careers', icon: Briefcase, key: 'company_careers' },
                { label: t('nav.press'), path: '/press', icon: Newspaper, key: 'company_press' }].
                filter((item) => {
                  // Travel-prefixed keys: feature flag only — no pmEnabled check
                  // (those pages aren't managed by the page-manager).
                  if (item.key === 'travel_ummrahPlanner') {
                    return features.ummrahPlanner !== false;
                  }
                  if (item.key === 'travel_destinations') {
                    // Destinations also requires at least one hotel provider
                    // (RateHawk, WebBeds OR Hotelbeds) — otherwise the page
                    // renders empty.
                    if (features.destinations === false) return false;
                    return features.ratehawk !== false || features.webbeds !== false || features.hotelbeds !== false;
                  }
                  // Existing company pages: feature + page-manager toggle.
                  // Always-available company pages (About, Careers, Press,
                  // Contact, FAQ) skip the feature check entirely — their
                  // visibility is governed solely by the Page Manager toggle.
                  const id = item.key.replace('company_', '');
                  if (ALWAYS_AVAILABLE_COMPANY_PAGES.has(id)) return pmEnabled[id] !== false;
                  return features[id] !== false && pmEnabled[id] !== false;
                }).map(({ label, path, icon: Icon }) =>
                <Link key={path} to={path} onClick={() => setActiveDropdown(null)}
                className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-primary transition-colors">
                                            <Icon size={15} className="text-slate-400" /> {label}
                                        </Link>
                )}
                                </div>
              }
                        </div>

                        {/* ── Help dropdown ── */}
                        <div className="relative" ref={helpDropdownRef}>
                            <button
                onClick={() => setActiveDropdown((d) => d === 'help' ? null : 'help')}
                className={`flex items-center gap-0.5 px-1 xl:px-1.5 py-2 text-[13.5px] xl:text-[14.5px] font-semibold tracking-[-0.012em] transition-colors ${
                useDarkText ? 'text-slate-700 dark:text-slate-200 hover:text-primary' : 'text-white/80 hover:text-white'}`
                }>
                
                                {t('nav.help')}
                                <ChevronDown size={14} className={`transition-transform duration-200 ${activeDropdown === 'help' ? 'rotate-180' : ''}`} />
                            </button>
                            {activeDropdown === 'help' &&
              <div className="absolute top-full left-0 mt-3 w-52 bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-100 dark:border-slate-800 py-2 z-50 animate-fade-in-down">
                                    {[
                { label: t('nav.contactUs'), path: '/contact', icon: Phone, key: 'help_contact' },
                { label: t('nav.support'), path: '/support', icon: HelpCircle, key: 'help_support' },
                { label: t('nav.faq'), path: '/faq', icon: MessageCircle, key: 'help_faq' }].
                filter((item) => {
                  const id = item.key.replace('help_', '');
                  return features[id] !== false && pmEnabled[id] !== false;
                }).map(({ label, path, icon: Icon }) =>
                <Link key={path} to={path} onClick={() => setActiveDropdown(null)}
                className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-primary transition-colors">
                                            <Icon size={15} className="text-slate-400" /> {label}
                                        </Link>
                )}
                                </div>
              }
                        </div>

                        {/* ── B2B dropdown ── */}
                        {features.b2b !== false && !isB2B &&
            <div className="relative" ref={b2bDropdownRef}>
                                <button
                onClick={() => setActiveDropdown((d) => d === 'b2b' ? null : 'b2b')}
                className={`flex items-center gap-0.5 px-1 xl:px-1.5 py-2 text-[13.5px] xl:text-[14.5px] font-semibold tracking-[-0.012em] transition-colors ${
                useDarkText ? 'text-slate-700 dark:text-slate-200 hover:text-primary' : 'text-white/80 hover:text-white'}`
                }>
                
                                    <Briefcase size={12} className="flex-shrink-0" />
                                    B2B
                                    <ChevronDown size={14} className={`transition-transform duration-200 ${activeDropdown === 'b2b' ? 'rotate-180' : ''}`} />
                                </button>
                                {activeDropdown === 'b2b' &&
              <div className="absolute top-full left-0 mt-3 w-52 bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-100 dark:border-slate-800 py-2 z-50 animate-fade-in-down">
                                        <Link to="/b2b/register" onClick={() => setActiveDropdown(null)}
                className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-primary transition-colors">
                                            <UserPlus size={15} className="text-slate-400" /> Register as Agent
                                        </Link>
                                        <Link to="/b2b/login" onClick={() => setActiveDropdown(null)}
                className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-primary transition-colors">
                                            <LogIn size={15} className="text-slate-400" /> Agent Login
                                        </Link>
                                    </div>
              }
                            </div>
            }

                        {user && isDashboardUser &&
            <Link to="/private"
            className={`px-1.5 py-2 text-[13px] font-semibold transition-colors ${
            location.pathname.includes('/private') ?
            useDarkText ? 'text-primary' : 'text-white' :
            useDarkText ? 'text-slate-700 dark:text-slate-200 hover:text-primary' : 'text-white/80 hover:text-white'}`
            }>
                                {t('nav.dashboard')}
                            </Link>
            }

                        {/* ── Right utility divider ── */}
                        <div className={`w-px h-5 mx-0.5 ${useDarkText ? 'bg-slate-200 dark:bg-slate-700' : 'bg-white/20'}`} />

                        {/* Right utility group — tightly packed */}
                        <div className="flex items-center gap-0">

                        {/* Currency Chip — text label only appears on 2xl+ so it
                   doesn't crowd the right edge on standard laptops. */}
                        <button
                onClick={() => setSelectorModal({ isOpen: true, type: 'currency' })}
                className={`flex items-center gap-1 2xl:gap-1.5 pl-1.5 pr-1.5 2xl:pr-3 py-1.5 rounded-full text-[13px] font-bold transition-all ${
                useDarkText ? 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800' :
                'text-white/80 hover:bg-white/10'}`
                }>
                
                            <div className="w-5 h-5 rounded-full overflow-hidden flex-shrink-0 border border-slate-300/30">
                                <img src={`https://flagcdn.com/w40/${currency.flag.toLowerCase()}.png`} alt={currency.code} className="w-full h-full object-cover" onError={(e) => {e.target.style.display = 'none';}} loading="lazy" decoding="async" />
                            </div>
                            <span className="hidden 2xl:inline notranslate" translate="no">{currency.code}</span>
                        </button>

                        {/* Language Selector — same 2xl rule for the code label. */}
                        <div className="relative" ref={langDropdownRef}>
                            <button
                  onClick={() => setLangDropdownOpen((o) => !o)}
                  className={`flex items-center gap-1 2xl:gap-1.5 pl-1.5 pr-1 2xl:pr-2 py-1.5 rounded-full text-[13px] font-bold transition-all ${
                  useDarkText ? 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800' :
                  'text-white/80 hover:bg-white/10'}`
                  }>
                  
                                <div className="w-5 h-5 rounded-full overflow-hidden flex-shrink-0 border border-slate-300/30">
                                    <img src={`https://flagcdn.com/w40/${currentLang.flag}.png`} alt={currentLang.label} className="w-full h-full object-cover" onError={(e) => {e.target.style.display = 'none';}} loading="lazy" decoding="async" />
                                </div>
                                <span className="hidden 2xl:inline uppercase notranslate" translate="no">{currentLang.code}</span>
                                <ChevronDown size={11} className="hidden 2xl:block opacity-50" />
                            </button>
                            {langDropdownOpen &&
                <div className="absolute right-0 top-full mt-2 w-44 bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-100 dark:border-slate-800 py-1.5 z-50 animate-fade-in-down">
                                    {LANGUAGES.map((lang) =>
                  <button
                    key={lang.code}
                    onClick={() => changeLang(lang.code)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors hover:bg-slate-50 dark:hover:bg-slate-800 ${
                    currentLang.code === lang.code ?
                    'text-primary bg-primary/5' :
                    'text-slate-700 dark:text-slate-300'}`
                    }>
                    
                                            <div className="w-5 h-5 rounded-full overflow-hidden flex-shrink-0 border border-slate-200 dark:border-slate-700">
                                                <img src={`https://flagcdn.com/w40/${lang.flag}.png`} alt={lang.label} className="w-full h-full object-cover" onError={(e) => {e.target.style.display = 'none';}} loading="lazy" decoding="async" />
                                            </div>
                                            {lang.label}
                                        </button>
                  )}
                                </div>
                }
                        </div>

                        {/* Theme Toggle */}
                        <button
                onClick={toggleTheme}
                className={`p-2 rounded-full transition-all ${
                useDarkText ? 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-700' :
                'text-white/70 hover:bg-white/10 hover:text-white'}`
                }>
                
                            {isDark ? <Sun size={20} /> : <Moon size={20} />}
                        </button>
                        {/* B2B Agent indicator */}
                        {isB2B &&
              <div className="flex items-center gap-1.5">
                                <Link
                  to="/b2b/portal"
                  className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white/95 dark:bg-slate-900/85 backdrop-blur-md ring-1 ring-primary/30 dark:ring-primary/40 hover:ring-primary/60 hover:bg-white dark:hover:bg-slate-800 text-primary dark:text-blue-300 font-semibold text-xs transition shadow-sm">
                  
                                    <Briefcase size={13} />
                                    <span className="notranslate" translate="no">{b2bAgent?.name?.split(' ')[0] || 'Agent'}</span>
                                    <span className="notranslate bg-primary text-white rounded-full px-1.5 py-0.5 text-[10px] font-bold" translate="no">B2B</span>
                                </Link>
                                <button
                  onClick={() => b2bLogout()}
                  className="text-xs text-slate-400 hover:text-red-500 transition p-1"
                  title="B2B Sign out">
                  
                                    <LogOut size={14} />
                                </button>
                            </div>
              }

                        {/* Auth Button — desktop */}
                        {activeProfile ? (
              /* ── Signed-in avatar + dropdown ── */
              <div className="relative" ref={profileDropdownRef}>
                                <button
                  onClick={() => setProfileDropdownOpen((o) => !o)}
                  className="w-9 h-9 rounded-full border-2 border-slate-200 dark:border-slate-700 hover:border-primary dark:hover:border-primary hover:shadow-md transition-all overflow-hidden flex items-center justify-center flex-shrink-0"
                  title={activeProfile.given_name || activeProfile.name}>
                  
                                    {activeProfile.picture ?
                  <img src={activeProfile.picture} alt={activeProfile.name} className="w-full h-full object-cover" referrerPolicy="no-referrer" loading="lazy" decoding="async" /> :

                  <div className="w-full h-full bg-accent flex items-center justify-center text-white font-bold text-sm">
                                            {(activeProfile.given_name?.[0] || activeProfile.name?.[0] || '?').toUpperCase()}
                                        </div>
                  }
                                </button>

                                {profileDropdownOpen &&
                <div className="absolute right-0 top-full mt-2 w-64 bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-100 dark:border-slate-800 overflow-hidden z-50 animate-fade-in-down">
                                        <div className="flex items-center gap-3 px-4 py-4 border-b border-slate-100 dark:border-slate-800">
                                            {activeProfile.picture ?
                    <img src={activeProfile.picture} alt={activeProfile.name} className="w-11 h-11 rounded-full object-cover flex-shrink-0" referrerPolicy="no-referrer" loading="lazy" decoding="async" /> :

                    <div className="w-11 h-11 rounded-full bg-accent flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                                                    {activeProfile.given_name?.[0] || activeProfile.name?.[0] || '?'}
                                                </div>
                    }
                                            <div className="min-w-0">
                                            <p className="notranslate font-semibold text-slate-900 dark:text-white text-sm truncate" translate="no">{activeProfile.name}</p>
                                            <p className="notranslate text-xs text-slate-500 dark:text-slate-400 truncate" translate="no">{activeProfile.email}</p>
                                            </div>
                                        </div>
                                        <div className="py-2 px-2">
                                            <Link
                      to="/my-bookings"
                      onClick={() => setProfileDropdownOpen(false)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 font-medium transition-colors">
                      
                                                <CalendarDays size={15} />
                                                {t('nav.myBookings')}
                                            </Link>
                                            <Link
                      to="/wishlist"
                      onClick={() => setProfileDropdownOpen(false)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 font-medium transition-colors">
                      
                                                <Heart size={15} />
                                                {t('nav.wishlist', 'Wishlist')}
                                            </Link>
                                            <button
                      onClick={() => {user ? signOut() : userSignOut();setProfileDropdownOpen(false);}}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40 font-medium transition-colors">
                      
                                                <LogOut size={15} />
                                                {t('nav.signOut')}
                                            </button>
                                        </div>
                                    </div>
                }
                            </div>) : (

              /* ── Sign In Google circle + dropdown ── */
              <div className="relative ml-1" ref={loginDropdownRef}>
                                <button
                  onClick={() => setLoginDropdownOpen((o) => !o)}
                  className="w-9 h-9 rounded-full border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-primary dark:hover:border-primary hover:shadow-md transition-all flex items-center justify-center flex-shrink-0"
                  title="Sign in">
                  
                                    <svg width="18" height="18" viewBox="0 0 48 48">
                                        <path fill="#EA4335" d="M24 9.5c3.14 0 5.95 1.08 8.17 2.84l6.1-6.1C34.46 3.19 29.52 1 24 1 14.82 1 7.07 6.48 3.64 14.22l7.1 5.52C12.5 13.67 17.8 9.5 24 9.5z" />
                                        <path fill="#4285F4" d="M46.52 24.5c0-1.56-.14-3.07-.4-4.5H24v8.5h12.67c-.55 2.95-2.2 5.44-4.68 7.12l7.14 5.54C43.37 37.19 46.52 31.36 46.52 24.5z" />
                                        <path fill="#FBBC05" d="M10.74 28.26A14.53 14.53 0 0 1 9.5 24c0-1.48.25-2.91.74-4.26l-7.1-5.52A23.93 23.93 0 0 0 0 24c0 3.86.92 7.5 2.55 10.72l8.19-6.46z" />
                                        <path fill="#34A853" d="M24 47c5.52 0 10.15-1.83 13.54-4.96l-7.14-5.54C28.5 38.07 26.36 38.5 24 38.5c-6.2 0-11.5-4.17-13.26-9.74l-8.19 6.46C6.07 42.48 14.48 47 24 47z" />
                                    </svg>
                                </button>

                                {loginDropdownOpen &&
                <div className="absolute right-0 top-full mt-2 w-72 bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-100 dark:border-slate-800 overflow-hidden z-50 animate-fade-in-down">
                                        <div className="px-5 pt-5 pb-3">
                                            <p className="text-base font-bold text-slate-900 dark:text-white mb-1">{t('nav.welcomeBack')}</p>
                                            <p className="text-xs text-slate-500 dark:text-slate-400">{t('nav.signInDesc')}</p>
                                        </div>
                                        <div className="px-4 pb-5">
                                            {googleClientId ?
                    <GoogleLoginBtn signIn={unifiedSignIn} onClose={(result) => {setLoginDropdownOpen(false);if (result?.isAdmin) navigate('/private');}} variant="desktop" t={t} /> :
                    <p className="text-xs text-center text-slate-400 py-2">Sign-in unavailable</p>
                    }
                                        </div>
                                    </div>
                }
                            </div>)
              }
                        </div>{/* end right utility group */}
                    </div>{/* end Desktop Links */}
                    <div className="flex items-center gap-2 lg:hidden ms-auto">
                        <button
              onClick={toggleTheme}
              aria-label={t('nav.toggleTheme', isDark ? 'Switch to light mode' : 'Switch to dark mode')}
              className={`p-2.5 rounded-full ${useDarkText ? 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-yellow-400' :
              'bg-white/10 text-white'}`
              }>
              
                            {isDark ? <Sun size={20} /> : <Moon size={20} />}
                        </button>
                        <button
              className={`p-2 rounded-lg transition-colors ${useDarkText ? 'text-slate-900 dark:text-white hover:bg-slate-100 dark:hover:bg-slate-800' : 'text-white hover:bg-white/10'}`}
              aria-label={isMobileMenuOpen ? t('common.close', 'Close') : t('nav.menu', 'Menu')}
              aria-expanded={isMobileMenuOpen}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>

                            {isMobileMenuOpen ?
              <X className={useDarkText ? 'text-slate-900 dark:text-white' : 'text-white'} /> :

              <Menu className={useDarkText ? 'text-slate-900 dark:text-white' : 'text-white'} />
              }
                        </button>
                    </div>
                </div>

                {/* Mobile Menu */}
                {isMobileMenuOpen &&
        <div className="hm-nav-mobile-menu absolute top-full left-0 w-full bg-white dark:bg-slate-900 shadow-2xl lg:hidden border-t border-slate-100 dark:border-slate-800 z-50 overflow-y-auto overscroll-contain max-h-[calc(100dvh-var(--navbar-h,64px)-var(--tib-height,0px))]">
                        <div className="px-4 py-4 flex flex-col gap-1">

                            {/* ── Explore section ── */}
                            <p className="text-[11px] font-bold text-slate-400 dark:text-slate-600 uppercase tracking-widest px-1 mb-1 mt-1">{t('nav.explore')}</p>
                            {[
            (features.ratehawk !== false && features.contentApi !== false || features.webbeds !== false) && { name: t('nav.hotels'), path: '/hotels', Icon: Hotel, bg: 'bg-blue-50 dark:bg-blue-900/25', ic: 'text-blue-600 dark:text-blue-400' },
            features.flights !== false && { name: t('nav.flights'), path: '/flights', Icon: Plane, bg: 'bg-sky-50 dark:bg-sky-900/25', ic: 'text-sky-600 dark:text-sky-400' },
            features.pkfareFlights !== false && { name: t('nav.pkfareFlights', 'Air Tickets'), path: '/pkfare-flights', Icon: Plane, bg: 'bg-indigo-50 dark:bg-indigo-900/25', ic: 'text-indigo-600 dark:text-indigo-400' },
            features.tours !== false && { name: t('nav.tours'), path: '/tours', Icon: Map, bg: 'bg-amber-50 dark:bg-amber-900/25', ic: 'text-amber-600 dark:text-amber-400' },
            (features.ratehawk !== false || features.webbeds !== false || features.hotelbeds !== false) && features.destinations !== false && { name: t('nav.destinations'), path: '/destinations', Icon: MapPin, bg: 'bg-green-50 dark:bg-green-900/25', ic: 'text-green-600 dark:text-green-400' },
            features.blog !== false && { name: t('nav.blog'), path: '/blog', Icon: BookOpen, bg: 'bg-purple-50 dark:bg-purple-900/25', ic: 'text-purple-600 dark:text-purple-400' }].
            filter(Boolean).map(({ name, path, Icon, bg, ic }) =>
            <Link key={name} to={path} onClick={() => setIsMobileMenuOpen(false)}
            className="flex items-center gap-3.5 px-1 py-3 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800/60 active:bg-slate-100 dark:active:bg-slate-800 transition-colors">
                                    <div className={`w-10 h-10 rounded-2xl ${bg} flex items-center justify-center flex-shrink-0`}>
                                        <Icon size={19} className={ic} />
                                    </div>
                                    <span className="flex-1 text-[15px] font-semibold text-slate-800 dark:text-white">{name}</span>
                                    <ChevronRight size={15} className="text-slate-300 dark:text-slate-600 flex-shrink-0" />
                                </Link>
            )}

                            {/* ── Services section ── */}
                            {(features.hajjUmrah !== false || features.offlineHotels !== false || features.visa !== false) &&
            <>
                                    <div className="h-px bg-slate-100 dark:bg-slate-800 my-2" />
                                    <p className="text-[11px] font-bold text-slate-400 dark:text-slate-600 uppercase tracking-widest px-1 mb-1">{t('nav.services')}</p>
                                </>
            }
                            {features.hajjUmrah !== false &&
            <Link to="/hajj-umrah" onClick={() => setIsMobileMenuOpen(false)}
            className="flex items-center gap-3.5 px-1 py-3 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800/60 active:bg-slate-100 dark:active:bg-slate-800 transition-colors">
                                    <div className="w-10 h-10 rounded-2xl bg-emerald-50 dark:bg-emerald-900/25 flex items-center justify-center flex-shrink-0">
                                        <Moon size={19} className="text-emerald-600 dark:text-emerald-400" />
                                    </div>
                                    <span className="flex-1 text-[15px] font-semibold text-slate-800 dark:text-white">{t('nav.hajjUmrah')}</span>
                                    <ChevronRight size={15} className="text-slate-300 dark:text-slate-600 flex-shrink-0" />
                                </Link>
            }
                            {features.ummrahPlanner !== false &&
            <Link to="/ummrah-planner" onClick={() => setIsMobileMenuOpen(false)}
            className="flex items-center gap-3.5 px-1 py-3 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800/60 active:bg-slate-100 dark:active:bg-slate-800 transition-colors">
                                    <div className="w-10 h-10 rounded-2xl bg-teal-50 dark:bg-teal-900/25 flex items-center justify-center flex-shrink-0">
                                        <Navigation size={19} className="text-teal-600 dark:text-teal-400" />
                                    </div>
                                    <span className="flex-1 text-[15px] font-semibold text-slate-800 dark:text-white">{t('nav.umrahPlanner')}</span>
                                    <ChevronRight size={15} className="text-slate-300 dark:text-slate-600 flex-shrink-0" />
                                </Link>
            }
                            {features.offlineHotels !== false &&
            <Link to="/hotels-partner" onClick={() => setIsMobileMenuOpen(false)}
            className="flex items-center gap-3.5 px-1 py-3 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800/60 active:bg-slate-100 dark:active:bg-slate-800 transition-colors">
                                    <div className="w-10 h-10 rounded-2xl bg-orange-50 dark:bg-orange-900/25 flex items-center justify-center flex-shrink-0">
                                        <Building2 size={19} className="text-orange-600 dark:text-orange-400" />
                                    </div>
                                    <span className="flex-1 text-[15px] font-semibold text-slate-800 dark:text-white">{t('nav.partnerHotels')}</span>
                                    <ChevronRight size={15} className="text-slate-300 dark:text-slate-600 flex-shrink-0" />
                                </Link>
            }
                            {features.visa !== false &&
            <Link to="/visa" onClick={() => setIsMobileMenuOpen(false)}
            className="flex items-center gap-3.5 px-1 py-3 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800/60 active:bg-slate-100 dark:active:bg-slate-800 transition-colors">
                                    <div className="w-10 h-10 rounded-2xl bg-red-50 dark:bg-red-900/25 flex items-center justify-center flex-shrink-0">
                                        <ShieldCheck size={19} className="text-red-500 dark:text-red-400" />
                                    </div>
                                    <span className="flex-1 text-[15px] font-semibold text-slate-800 dark:text-white">{t('nav.visaInfo')}</span>
                                    <ChevronRight size={15} className="text-slate-300 dark:text-slate-600 flex-shrink-0" />
                                </Link>
            }

                            {/* ── Company accordion ── */}
                            <div className="h-px bg-slate-100 dark:bg-slate-800 my-2" />
                            <div>
                                <button onClick={() => setActiveDropdown((d) => d === 'mCompany' ? null : 'mCompany')}
              className="flex items-center gap-3.5 w-full px-1 py-3 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors">
                                    <div className="w-10 h-10 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
                                        <Briefcase size={19} className="text-slate-500 dark:text-slate-400" />
                                    </div>
                                    <span className="flex-1 text-start text-[15px] font-semibold text-slate-800 dark:text-white">{t('nav.company')}</span>
                                    <ChevronDown size={16} className={`text-slate-400 transition-transform duration-200 flex-shrink-0 ${activeDropdown === 'mCompany' ? 'rotate-180' : ''}`} />
                                </button>
                                {activeDropdown === 'mCompany' &&
              <div className="ms-[54px] flex flex-col gap-0.5 mb-1">
                                        {[
                { label: t('nav.aboutUs'), path: '/about', icon: BookOpen, key: 'company_about' },
                { label: t('nav.careers'), path: '/careers', icon: Briefcase, key: 'company_careers' },
                { label: t('nav.press'), path: '/press', icon: Newspaper, key: 'company_press' }].
                filter((item) => {
                  const id = item.key.replace('company_', '');
                  if (ALWAYS_AVAILABLE_COMPANY_PAGES.has(id)) return pmEnabled[id] !== false;
                  return features[id] !== false && pmEnabled[id] !== false;
                }).map(({ label, path, icon: Icon }) =>
                <Link key={path} to={path}
                className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-primary transition-colors"
                onClick={() => {setActiveDropdown(null);setIsMobileMenuOpen(false);}}>
                                                <Icon size={14} className="text-slate-400 flex-shrink-0" /> {label}
                                            </Link>
                )}
                                    </div>
              }
                            </div>

                            {/* ── Help accordion ── */}
                            <div>
                                <button onClick={() => setActiveDropdown((d) => d === 'mHelp' ? null : 'mHelp')}
              className="flex items-center gap-3.5 w-full px-1 py-3 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors">
                                    <div className="w-10 h-10 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
                                        <HelpCircle size={19} className="text-slate-500 dark:text-slate-400" />
                                    </div>
                                    <span className="flex-1 text-start text-[15px] font-semibold text-slate-800 dark:text-white">{t('nav.helpSupport')}</span>
                                    <ChevronDown size={16} className={`text-slate-400 transition-transform duration-200 flex-shrink-0 ${activeDropdown === 'mHelp' ? 'rotate-180' : ''}`} />
                                </button>
                                {activeDropdown === 'mHelp' &&
              <div className="ms-[54px] flex flex-col gap-0.5 mb-1">
                                        {[
                { label: t('nav.contactUs'), path: '/contact', icon: Phone, key: 'help_contact' },
                { label: t('nav.support'), path: '/support', icon: HelpCircle, key: 'help_support' },
                { label: t('nav.faq'), path: '/faq', icon: MessageCircle, key: 'help_faq' }].
                filter((item) => {
                  const id = item.key.replace('help_', '');
                  return features[id] !== false && pmEnabled[id] !== false;
                }).map(({ label, path, icon: Icon }) =>
                <Link key={path} to={path}
                className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-primary transition-colors"
                onClick={() => {setActiveDropdown(null);setIsMobileMenuOpen(false);}}>
                                                <Icon size={14} className="text-slate-400 flex-shrink-0" /> {label}
                                            </Link>
                )}
                                    </div>
              }
                            </div>

                            {/* ── B2B Agent (accordion, hidden when already logged in as B2B) ── */}
                            {features.b2b !== false && !isB2B &&
            <div>
                                    <button onClick={() => setActiveDropdown((d) => d === 'mB2B' ? null : 'mB2B')}
              className="flex items-center gap-3.5 w-full px-1 py-3 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors">
                                        <div className="w-10 h-10 rounded-2xl bg-blue-50 dark:bg-blue-900/25 flex items-center justify-center flex-shrink-0">
                                            <Briefcase size={19} className="text-blue-600 dark:text-blue-400" />
                                        </div>
                                        <span className="flex-1 text-start text-[15px] font-semibold text-slate-800 dark:text-white">B2B Agent</span>
                                        <ChevronDown size={16} className={`text-slate-400 transition-transform duration-200 flex-shrink-0 ${activeDropdown === 'mB2B' ? 'rotate-180' : ''}`} />
                                    </button>
                                    {activeDropdown === 'mB2B' &&
              <div className="ms-[54px] flex flex-col gap-0.5 mb-1">
                                            <Link to="/b2b/register"
                className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-primary transition-colors"
                onClick={() => {setActiveDropdown(null);setIsMobileMenuOpen(false);}}>
                                                <UserPlus size={14} className="text-slate-400 flex-shrink-0" /> Register as Agent
                                            </Link>
                                            <Link to="/b2b/login"
                className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-primary transition-colors"
                onClick={() => {setActiveDropdown(null);setIsMobileMenuOpen(false);}}>
                                                <LogIn size={14} className="text-slate-400 flex-shrink-0" /> Agent Login
                                            </Link>
                                        </div>
              }
                                </div>
            }

                            {/* ── Dashboard (admin only) ── */}
                            {user && isDashboardUser &&
            <Link to="/private" onClick={() => setIsMobileMenuOpen(false)}
            className="flex items-center gap-3.5 px-1 py-3 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors">
                                    <div className="w-10 h-10 rounded-2xl bg-violet-50 dark:bg-violet-900/25 flex items-center justify-center flex-shrink-0">
                                        <ShieldCheck size={19} className="text-violet-600 dark:text-violet-400" />
                                    </div>
                                    <span className="flex-1 text-[15px] font-semibold text-slate-800 dark:text-white">{t('nav.dashboard')}</span>
                                    <ChevronRight size={15} className="text-slate-300 dark:text-slate-600 flex-shrink-0" />
                                </Link>
            }

                            {/* ── For Agencies ── */}
                            {process.env.REACT_APP_SHOW_FOR_AGENCIES === 'true' &&
            <a href={process.env.REACT_APP_MARKETING_URL || 'https://travorahub.com/'} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-3.5 px-1 py-3 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors"
            onClick={() => setIsMobileMenuOpen(false)}>
                                    <div className="w-10 h-10 rounded-2xl bg-indigo-50 dark:bg-indigo-900/25 flex items-center justify-center flex-shrink-0">
                                        <Building2 size={19} className="text-indigo-600 dark:text-indigo-400" />
                                    </div>
                                    <span className="flex-1 text-[15px] font-semibold text-slate-800 dark:text-white">{t('nav.forAgencies')}</span>
                                    <ChevronRight size={15} className="text-slate-300 dark:text-slate-600 flex-shrink-0" />
                                </a>
            }

                            {/* ── Settings: Currency & Residency ── */}
                            <div className="h-px bg-slate-100 dark:bg-slate-800 my-2" />
                            <p className="text-[11px] font-bold text-slate-400 dark:text-slate-600 uppercase tracking-widest px-1 mb-2">{t('nav.settings')}</p>
                            <div className="grid grid-cols-2 gap-2">
                                <button
                onClick={() => {setSelectorModal({ isOpen: true, type: 'currency' });setIsMobileMenuOpen(false);}}
                className="flex items-center gap-2.5 px-3 py-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-primary hover:bg-primary/5 transition-colors">
                                    <div className="w-7 h-7 rounded-full overflow-hidden flex-shrink-0 border border-slate-200 dark:border-slate-600 bg-slate-800">
                                        <img src={`https://flagcdn.com/w40/${currency.flag.toLowerCase()}.png`} alt={currency.code} className="w-full h-full object-cover" onError={(e) => {e.target.style.display = 'none';}} loading="lazy" decoding="async" />
                                    </div>
                                    <div className="min-w-0 text-start">
                                        <p className="notranslate text-xs font-bold text-slate-700 dark:text-slate-200" translate="no">{currency.code}</p>
                                        <p className="notranslate text-xs text-slate-400" translate="no">{currency.symbol}</p>
                                    </div>
                                </button>
                                <button
                onClick={() => {setSelectorModal({ isOpen: true, type: 'residency' });setIsMobileMenuOpen(false);}}
                className="flex items-center gap-2.5 px-3 py-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-primary hover:bg-primary/5 transition-colors">
                                    <div className="w-7 h-7 rounded-full overflow-hidden flex-shrink-0 border border-slate-200 dark:border-slate-600 bg-slate-800">
                                        <img src={`https://flagcdn.com/w40/${residency.flag.toLowerCase()}.png`} alt={residency.name} className="w-full h-full object-cover" onError={(e) => {e.target.style.display = 'none';}} loading="lazy" decoding="async" />
                                    </div>
                                    <div className="min-w-0 text-start">
                                        <p className="notranslate text-xs font-bold text-slate-700 dark:text-slate-200" translate="no">{residency.code.toUpperCase()}</p>
                                        <p className="notranslate text-xs text-slate-400 truncate" translate="no">{residency.name}</p>
                                    </div>
                                </button>
                            </div>

                            {/* ── Language Selector (Mobile) ── */}
                            <div className="relative mt-2" ref={langDropdownRef}>
                                <button
                onClick={() => setLangDropdownOpen((o) => !o)}
                className="w-full flex items-center gap-2.5 px-3 py-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-primary hover:bg-primary/5 transition-colors">
                                    <span className="text-lg flex-shrink-0">{currentLang.flag}</span>
                                    <div className="min-w-0 text-start flex-1">
                                        <p className="text-xs font-bold text-slate-700 dark:text-slate-200">{t('nav.language')}</p>
                                        <p className="text-xs text-slate-400">{currentLang.label}</p>
                                    </div>
                                    <ChevronDown size={14} className={`text-slate-400 transition-transform ${langDropdownOpen ? 'rotate-180' : ''}`} />
                                </button>
                                {langDropdownOpen &&
              <div className="mt-1.5 grid grid-cols-2 gap-1.5 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-2 shadow-lg">
                                        {LANGUAGES.map((l) =>
                <button key={l.code} onClick={() => changeLang(l.code)}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${l.code === currentLang.code ? 'bg-primary/10 text-primary' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'}`}>
                                                <span className="text-base">{l.flag}</span>
                                                {l.label}
                                            </button>
                )}
                                    </div>
              }
                            </div>

                            {/* ── User card / Sign-in ── */}
                            <div className="h-px bg-slate-100 dark:bg-slate-800 my-2" />
                            {activeProfile ?
            <div className="rounded-2xl bg-slate-50 dark:bg-slate-800 overflow-hidden border border-slate-100 dark:border-slate-700">
                                    <div className="flex items-center gap-3 p-4">
                                        {activeProfile.picture ?
                <img src={activeProfile.picture} alt={activeProfile.name} className="w-11 h-11 rounded-full object-cover flex-shrink-0 ring-2 ring-white dark:ring-slate-700" referrerPolicy="no-referrer" loading="lazy" decoding="async" /> :

                <div className="w-11 h-11 rounded-full bg-accent flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                                                {activeProfile.given_name?.[0] || '?'}
                                            </div>
                }
                                        <div className="flex-1 min-w-0">
                                            <p className="notranslate font-bold text-slate-900 dark:text-white text-sm truncate" translate="no">{activeProfile.name}</p>
                                            <p className="notranslate text-xs text-slate-400 truncate" translate="no">{activeProfile.email}</p>
                                        </div>
                                    </div>
                                    <div className="border-t border-slate-200 dark:border-slate-700 px-3 py-2 flex gap-2">
                                        <Link to="/my-bookings" onClick={() => setIsMobileMenuOpen(false)}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-700 hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors">
                                            <CalendarDays size={15} /> {t('nav.myBookings')}
                                        </Link>
                                        <Link to="/wishlist" onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-700 hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors">
                                            <Heart size={15} />
                                        </Link>
                                        <button onClick={() => {user ? signOut() : userSignOut();setIsMobileMenuOpen(false);}}
                className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-red-500 bg-red-50 dark:bg-red-950/40 hover:bg-red-100 dark:hover:bg-red-950/60 transition-colors">
                                            <LogOut size={15} />
                                        </button>
                                    </div>
                                </div> :

            googleClientId ?
            <GoogleLoginBtn signIn={unifiedSignIn} onClose={(result) => {setIsMobileMenuOpen(false);if (result?.isAdmin) navigate('/private');}} variant="mobile" t={t} /> :
            null
            }

                            {/* ── Install App (PWA) ── */}
                            {/* Show when: browser says installable, already installed, OR in dev mode for preview */}
                            {!inNativeApp && (offerApk || canInstall || isInstalled || process.env.NODE_ENV === 'development') &&
            <div className="mt-2">
                                    <div className="h-px bg-slate-100 dark:bg-slate-800 mb-2" />
                                    {offerApk ?
              <a
                href={apkUrl}
                download
                rel="noopener"
                onClick={() => setIsMobileMenuOpen(false)}
                className="w-full flex items-center gap-3 px-3 py-3 rounded-2xl bg-primary/10 hover:bg-primary/15 border border-primary/20 transition-colors active:scale-[0.98]">
                                            <div className="w-9 h-9 rounded-xl overflow-hidden flex-shrink-0 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex items-center justify-center">
                                                {brand.logoUrl ?
                  <img src={brand.logoUrl} alt={brand.siteName} className="w-full h-full object-contain" loading="lazy" decoding="async" /> :
                  <span className="font-bold text-base text-primary">{(brand.siteName || 'A')[0].toUpperCase()}</span>
                  }
                                            </div>
                                            <div className="flex-1 min-w-0 text-start">
                                                <p className="text-sm font-bold text-primary">{t('nav.getAndroidApp', 'Download our Android app')}</p>
                                                <p className="notranslate text-xs text-slate-400 truncate" translate="no">{brand.siteName || 'App'}</p>
                                            </div>
                                            <Download size={16} className="text-primary flex-shrink-0" />
                                        </a> :
                                    isInstalled ?
              <div className="flex items-center gap-3 px-3 py-3 rounded-2xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-100 dark:border-emerald-900/40">
                                            <div className="w-9 h-9 rounded-xl overflow-hidden flex-shrink-0 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex items-center justify-center">
                                                {brand.logoUrl ?
                  <img src={brand.logoUrl} alt={brand.siteName} className="w-full h-full object-contain" loading="lazy" decoding="async" /> :

                  <span className="font-bold text-base text-primary">{(brand.siteName || 'A')[0].toUpperCase()}</span>
                  }
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs font-bold text-emerald-700 dark:text-emerald-400">App Installed ✓</p>
                                                <p className="notranslate text-xs text-slate-400 truncate" translate="no">{brand.siteName || 'App'}</p>
                                            </div>
                                        </div> :

              <button
                onClick={async () => {await install();setIsMobileMenuOpen(false);}}
                className="w-full flex items-center gap-3 px-3 py-3 rounded-2xl bg-primary/10 hover:bg-primary/15 border border-primary/20 transition-colors active:scale-[0.98]">
                
                                            <div className="w-9 h-9 rounded-xl overflow-hidden flex-shrink-0 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex items-center justify-center">
                                                {brand.logoUrl ?
                  <img src={brand.logoUrl} alt={brand.siteName} className="w-full h-full object-contain" loading="lazy" decoding="async" /> :

                  <span className="font-bold text-base text-primary">{(brand.siteName || 'A')[0].toUpperCase()}</span>
                  }
                                            </div>
                                            <div className="flex-1 min-w-0 text-start">
                                                <p className="text-sm font-bold text-primary">Download App</p>
                                                <p className="notranslate text-xs text-slate-400 truncate" translate="no">{brand.siteName || 'Install App'}</p>
                                            </div>
                                            <Download size={16} className="text-primary flex-shrink-0" />
                                        </button>
              }
                                </div>
            }

                            {/* Bottom padding for safe area */}
                            <div className="h-4" />
                        </div>
                    </div>
        }
            </nav>

            {selectorModal.isOpen &&
            <React.Suspense fallback={null}>
            <GlobalSelectorModal
        isOpen={selectorModal.isOpen}
        onClose={() => setSelectorModal({ ...selectorModal, isOpen: false })}
        currentType={selectorModal.type}
        onSelect={handleGlobalSelect}
        currentCurrency={currency}
        currentResidency={residency} />
            </React.Suspense>}
      
        </>);

};

export default Navbar;